import { useCallback, useMemo } from 'react';
import qs from 'qs';
import history from './history';
import type { QueryObject, RouterProps } from 'interface/queryParam';

const EMPTY_QUERY: QueryObject = {};

/**
 * Parse query params from url string.
 *
 * @example
 * parse('foo=bar&goo=car&hoo=dar')
 * => {foo: 'bar', goo: 'car', hoo: 'dar'}
 *
 * @param value
 */
export function parse(value: string, options: qs.IParseOptions = {}): qs.ParsedQs {
  return qs.parse(value.replace('?', ''), options) as qs.ParsedQs;
}

/**
 * Stringify to url with query params.
 *
 * @example
 * stringify('link', { foo: 'bar', goo: 'car', hoo: 'dar' })
 * => 'link?foo=bar&goo=car&hoo=bar'
 *
 * @param route
 * @param params
 * @param [options={}] -options for qs stringify
 */

export function stringify(route: string, params: QueryObject, options: qs.IStringifyOptions = {}): string {
  if (params === null || !(params instanceof Object) || Object.keys(params).length < 1) {
    return route;
  }

  return route + '?' + qs.stringify(params, options);
}

/**
 * Update url with query params.
 *
 * @param props
 * @param value
 */
export const updateUrl = (props: RouterProps, value: QueryObject): void => {
  const queryParam = stringify('', value, { arrayFormat: 'comma', encode: false });
  history.push({
    pathname: props.location.pathname,
    search: queryParam,
  });
};

export const parseQueryURL = (
  query: string,
  { convertToArray = false, typeConversion = false }: { convertToArray?: boolean; typeConversion?: boolean } = {}
): QueryObject => {
  if (!query) {
    return {};
  }

  return query
    .substring(1, query.length)
    .split('&')
    .reduce((acc, curr) => {
      const [key, value] = curr.split('=');
      const convertedValue = value && value.replace(/%20/g, ' ');

      if (!convertToArray || !convertedValue) {
        return {
          ...acc,
          [key]: convertedValue,
        };
      }

      if (!value.includes(',')) {
        return {
          ...acc,
          [key]: typeConversion ? Number(convertedValue) || convertedValue : convertedValue,
        };
      }

      const valueInArray = convertedValue.split(',');

      if (!typeConversion) {
        return {
          ...acc,
          [key]: valueInArray,
        };
      }

      const typeConverted = valueInArray.map((item) => Number(item) || item);

      return {
        ...acc,
        [key]: typeConverted,
      };
    }, {});
};

export const useQuery = (
  props: RouterProps,
  initialState: QueryObject = EMPTY_QUERY
): [QueryObject, (query: QueryObject) => void] => {
  const { history: routerHistory, location } = props;

  const query = useMemo(() => {
    const parsedQuery = parseQueryURL(location.search);

    return Object.keys(parsedQuery).length ? parsedQuery : initialState;
  }, [initialState, location.search]);

  const setQuery = useCallback(
    (query) => {
      const queryParam = stringify('', query, { arrayFormat: 'comma', encode: false });

      if (location.search === queryParam) {
        return;
      }

      routerHistory.push({
        search: queryParam,
      });
    },
    [location.search, routerHistory]
  );

  return [query, setQuery];
};

/**
 * Adds query parameters to a given URL.
 *
 * @param url - The base URL to which the query parameters will be added.
 * @param params - An object representing the query parameters as key-value pairs.
 * @returns The URL with the query parameters appended.
 *
 * @example
 * // Returns 'https://example.com/path?q=saugat'
 * getUrlWithQueryParams('https://example.com/path', { q: 'saugat' });
 *
 * @example
 * // Returns 'https://example.com/path?search=test&sort=asc'
 * getUrlWithQueryParams('https://example.com/path', { search: 'test', sort: 'asc' });
 */
export function getUrlWithQueryParams(url: string, params: Record<string, string>): string {
  const queryString = new URLSearchParams(params).toString();

  return `${url}?${queryString}`;
}
